<template>
  <div class="station-charts">
    <h2>Station Data</h2>

    <!-- Filters Section -->
    <div class="filters">
      <div class="filter-item">
        <label for="station-select">Select Station:</label>
        <select id="station-select" v-model="selectedStationId" @change="fetchStationData">
          <option v-for="station in stations" :key="station.ID" :value="station.ID">
            {{ station.NAME }}
          </option>
        </select>
      </div>

      <div class="filter-item">
        <label>Select Date:</label>
        <Datepicker
          ref="datepicker"
          v-model="picked"
          :style="{
            'width': '100%', 'padding': '8px', ' font-size': '1em',
            'border-radius': '5px',
            'border': '1px solid #ccc',
            'appearance': 'none'
          }"
          type="date"
          value-type="YYYY-MM-DD"
          @update:modelValue="handleDateChange"
        />
      </div>
      <div>
        <DownloadDailyFolder :stationId="selectedStationId" :date="formattedDate" />
      </div>
    </div>

    <!-- Charts Layout -->
    <div class="charts-layout">
      <!-- Left Column (3 Rows) -->
      <div class="column-left">
        <div class="chart-container">
          <h3>Cycle Time</h3>
          <CycleLengthChart :labels="labels" :cycleLengthData="cycleLengthData" />
        </div>

        <div class="chart-container">
          <h3>Pressure Chart</h3>
          <PressureChart :labels="labels" :cycleIDs="cycleIDs" :datasets="pressureDatasets" @cycle-selected="handleCycleSelected"/>
        </div>

        <div class="chart-container">
          <h3>Temperature Chart</h3>
          <TemperatureChart :labels="labels" :cycleIDs="cycleIDs" :datasets="temperatureDatasets" @cycle-selected="handleCycleSelected"/>
        </div>
      </div>

      <!-- Right Column (2 Rows) -->
      <div class="column-right">
        <div class="chart-container large-chart">
          <h3>Pressure Signals Chart</h3>
          <MultiSignalChart :signals="pressureSignals" />
        </div>

        <div class="chart-container small-chart">
          <h3>Temperature Signal Chart</h3>
          <MultiSignalChart :signals="temperatureSignals" />
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import { callProtectedApi } from '../api'; // Import the callProtectedApi function
import Datepicker from 'vue3-datepicker';
import PressureChart from './PressureChart.vue';
import TemperatureChart from './TemperatureChart.vue';
import CycleLengthChart from './CycleLengthChart.vue';
import MultiSignalChart from './MultiSignalChart.vue';
import DownloadDailyFolder from './DownloadDailyFolder.vue';

export default {
  name: 'UserHome',
  components: { Datepicker, PressureChart, TemperatureChart, CycleLengthChart, MultiSignalChart, DownloadDailyFolder },
  data() {
    return {
      picked: new Date(),
      pressureSignals: [],
      temperatureSignals: [],
      stations: [],
      selectedStationId: null,
      cycleIDs: [],
      labels: [],
      cycleLengthData: [],
      pressureChannels: [
        { label: 'Pressure 1', enabled: true, data: [], color: 'red' },
        { label: 'Pressure 2', enabled: true, data: [], color: 'blue' },
        { label: 'Pressure 3', enabled: true, data: [], color: 'green' },
        { label: 'Pressure 4', enabled: true, data: [], color: 'orange' },
      ],
      temperatureChannels: [
        { label: 'Temperature 1', enabled: true, data: [], color: 'red' },
        { label: 'Temperature 2', enabled: true, data: [], color: 'blue' },
        { label: 'Temperature 3', enabled: true, data: [], color: 'green' },
        { label: 'Temperature 4', enabled: true, data: [], color: 'orange' },
      ],
    };
  },
  computed: {
    formattedDate() {
      const day = String(this.picked.getDate()).padStart(2, '0');
      const month = String(this.picked.getMonth() + 1).padStart(2, '0'); // Months are 0-based
      const year = this.picked.getFullYear();
      return `${year}-${month}-${day}`;
  },
    pressureDatasets() {
      return this.pressureChannels
        .filter((channel) => channel.enabled)
        .map((channel) => ({
          label: channel.label,
          data: channel.data,
          borderColor: channel.color,
          fill: false,
        }));
    },
    temperatureDatasets() {
      return this.temperatureChannels
        .filter((channel) => channel.enabled)
        .map((channel) => ({
          label: channel.label,
          data: channel.data,
          borderColor: channel.color,
          fill: false,
        }));
    },
    apiUrl() {
      return process.env.VUE_APP_API_BASE_URL;  // Access the environment variable for API base URL
    },
  },
  async created() {
    await this.fetchStations();
  },
  methods: {
    formatDate(date) {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
      const year = date.getFullYear();
      return `${year}-${month}-${day}`;
  },
    handleDateChange(date) {
      this.picked = date; // Update the picked date
      this.fetchStationData(); // Call your data-fetching method

      setTimeout(() => {
      const inputElement = this.$refs.datepicker.$el.querySelector('input');
      if (inputElement) {
        inputElement.blur(); // Blur the input element directly
      }
    }, 100); // Adjust the delay if needed

    },
    async fetchStations() {
      try {
        const response = await callProtectedApi(`${this.apiUrl}/api/stations`, 'get');
        this.stations = response.data;
        if (this.stations.length > 0) {
          this.selectedStationId = this.stations[0].ID;
          await this.fetchStationData();
        }
      } catch (error) {
        console.error('Error fetching stations:', error);
      }
    },
    async fetchStationData() {
      if (!this.selectedStationId) return;

      try {
        const startDate = this.picked;
        const endDate = new Date(startDate.getTime() + 24 * 60 * 60 * 1000);

        const response = await callProtectedApi(`${this.apiUrl}/api/detailed-charts/${this.selectedStationId}`, 
        'post', {
          startDate: this.formatDate(startDate),
          endDate: this.formatDate(endDate),
        });

        const data = response.data;
        this.labels = data.map((entry) => entry.DateTime);
        this.cycleIDs = data.map((entry) => entry.CycleID);
        this.cycleLengthData = data.map((entry) => entry.CycleLength);

        this.pressureChannels.forEach((channel, index) => {
          channel.data = data.map((entry) => entry[`Pressure${index + 1}`]);
        });

        this.temperatureChannels.forEach((channel, index) => {
          channel.data = data.map((entry) => entry[`Temperature${index + 1}`]);
        });
      } catch (error) {
        console.error('Error fetching station data:', error);
      }
    },
    async handleCycleSelected(cycleId) {
      const signals = await this.fetchSignalFiles(this.selectedStationId, cycleId);
      this.pressureSignals = signals.filter(signal => signal.channelNo >= 4);
      this.temperatureSignals = signals.filter(signal => signal.channelNo < 4);
    },

    async fetchSignalFiles(stationId, cycleNo) {
      try {
        
        const response = await callProtectedApi(`${this.apiUrl}/api/signal-files`, 'post', {
          stationId,
          cycleNo,
        });

        if (response.data && response.data.signals) {
          return response.data.signals; // Return the array of signal data
        } else {
          console.error('No data received from the server');
          return [];
        }
      } catch (error) {
        console.error('Error fetching signal files:', error);
        return [];
      }
    },
  },
};
</script>

<style scoped>
.station-charts {
  display: flex;
  flex-direction: column;
  padding: 20px;
}

/* Two-Column Layout */
.charts-layout {
  display: flex;
  gap: 20px;
  height: calc(100vh - 150px); /* Adjust height to fit screen */
}

/* Left Column (3-row charts) */
.column-left {
  flex: 3; /* Takes 50% width */
  display: flex;
  flex-direction: column;
  gap: 15px;
}

/* Right Column (2-row charts) */
.column-right {
  flex: 2; /* Takes 50% width */
  display: flex;
  flex-direction: column;
  gap: 15px;
}

/* Chart Containers */
.chart-container {
  background: white;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* Filters Section */
.filters {
  display: flex;
  justify-content: space-between;
  gap: 30px;
  margin-bottom: 30px;
  max-width: 900px;
  margin: 15px;
}

.filter-item {
  font-size: 1.2em;
  flex: 1;
}

.filter-item label {
  display: block;
  margin-bottom: 5px;
}

.filter-item select {
  width: 100%;
  padding: 8px;
  font-size: 1em;
  border-radius: 5px;
  border: 1px solid #ccc;
  appearance: none;
  /* Make sure dropdowns look consistent */
}

.datepickerinputcustom {
  width: 100%;
  padding: 8px;
  font-size: 1em;
  border-radius: 5px;
  border: 1px solid #ccc;
  appearance: none;
  /* Make sure dropdowns look consistent */
}


/* Make it responsive */
@media (max-width: 768px) {
  .filters {
    flex-direction: column;
  }

  .charts-layout {
    flex-direction: column;
  }

  .column-left, .column-right {
    flex: none;
    width: 100%;
  }

}


</style>
